// Shared ICD-10 review/edit components.
//
// Originally defined inline in `src/pages/ReferralIntake.jsx`. The CRM
// Queue's "Please Review" flag is driven by the same `icd10_review` bool
// and points at the same patient (by `bq_file_id`), but the queue had
// no UI for actually doing the review — so the badge was effectively
// non-actionable for queue patients. Extracting these three components
// into a single shared module lets the CRM Queue render the panel in
// its detail view without duplicating any of this logic. Single source
// of truth.
//
// Public surface (named exports):
//   • Icd10ReviewBadge({ show })
//       — Red "Please Review" pill.
//   • Icd10SearchBox({ onSelect, excludeCodes, disabled })
//       — Debounced typeahead against GET /api/intake/icd10/search.
//   • Icd10Panel({ codes, review, fileId, userRole, onUpdate })
//       — View/edit panel. Save → PUT
//         /api/intake/referrals/{fileId}/icd10.
//
// Permissions (deliberate, matches the prior intake-inline behaviour):
//   • Edit allowed when `userRole` is "admin" or "sales".
//   • Everyone else gets read-only — no Edit button rendered.
//   • Caller does NOT need to compute a separate gate (this is
//     intentionally NOT coupled to intake's `canEditFields` which keys
//     off `intakeStatus.ready_for_crm` — that gate is for OTHER
//     intake-only fields and is irrelevant in the CRM Queue).

import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Search, Edit2, Loader2, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Mirrors the helper that lived in ReferralIntake.jsx. Token sourced
// from session first, falling back to localStorage so a "remember me"
// session also works.
const getStoredToken = () =>
  sessionStorage.getItem("token") || localStorage.getItem("token");

// Same codes in the same order? Used to retire the local post-save
// override once the server's read path has caught up.
export const sameCodeList = (a, b) => {
  const x = (a || []).map((c) => c.code);
  const y = (b || []).map((c) => c.code);
  return x.length === y.length && x.every((c, i) => c === y[i]);
};


// "ICD-10 Review" — red badge driven by the backend `icd10_review` bool
// (low-confidence OR non-null review note OR codes failed to parse).
// Single source of truth lives in `services.icd10_service
// .needs_icd10_review_flag` on the backend. Renamed from "Please Review"
// (item 3, 2026-09-20) — the badge names what is being reviewed.
export function Icd10ReviewBadge({ show }) {
  if (!show) return null;
  return (
    <span
      data-testid="icd10-review-badge"
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold text-xs bg-red-100 text-red-700"
      title="ICD-10 codes not yet confirmed. Open the ICD-10 panel to confirm."
    >
      <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
      ICD-10 Review
    </span>
  );
}


// Debounced typeahead used by `Icd10Panel`'s edit mode. Hits the
// backend reference search; falls back to "Search all" toggle when the
// limited-set match misses.
export function Icd10SearchBox({ onSelect, excludeCodes, disabled }) {
  const [q, setQ] = useState("");
  const [includeAll, setIncludeAll] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q.trim()) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/api/intake/icd10/search`, {
          params: { q, include_all: includeAll },
          headers: { Authorization: `Bearer ${getStoredToken()}` },
          withCredentials: true,
        });
        const exclude = new Set(excludeCodes || []);
        setResults((res.data?.results || []).filter((r) => !exclude.has(r.code)));
      } catch (err) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q, includeAll, excludeCodes]);

  return (
    <div className="space-y-2" data-testid="icd10-search-box">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            disabled={disabled}
            placeholder="Search ICD-10 by description or code…"
            data-testid="icd10-search-input"
            className="w-full pl-8 pr-3 h-9 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 disabled:bg-slate-50"
          />
        </div>
        <label className="flex items-center gap-1.5 text-xs text-slate-500 whitespace-nowrap">
          <input
            type="checkbox"
            checked={includeAll}
            onChange={(e) => setIncludeAll(e.target.checked)}
            data-testid="icd10-search-include-all"
          />
          Search all
        </label>
      </div>
      {loading && <p className="text-xs text-slate-400 italic">Searching…</p>}
      {!loading && q.trim() && results.length === 0 && (
        <p className="text-xs text-slate-400 italic" data-testid="icd10-search-empty">
          No matches. Try toggling &quot;Search all&quot;.
        </p>
      )}
      {results.length > 0 && (
        <ul
          className="max-h-56 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100"
          data-testid="icd10-search-results"
        >
          {results.map((r) => (
            <li key={r.code}>
              <button
                type="button"
                onClick={() => { onSelect(r); setQ(""); setResults([]); }}
                className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm flex gap-3"
                data-testid={`icd10-search-result-${r.code}`}
              >
                <span className="font-mono font-semibold text-slate-900 min-w-[80px]">{r.code}</span>
                <span className="text-slate-600 flex-1">{r.long_desc}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


// ICD-10 view/edit panel.
//   • View mode: parsed list, red "Please Review" badge.
//   • Edit mode (admin/sales only): add via debounced search box,
//     remove via X, reorder via ↑/↓. First item = primary.
//   • Save → PUT /api/intake/referrals/{file_id}/icd10. Backend
//     validates each code against the in-memory reference cache and
//     dispatches the BigQuery UPDATE as a FastAPI BackgroundTask.
//     Webhook firing (Phase 3) is gated separately by
//     ICD10_PATCH_ENABLED on the backend.
//   • Cancel discards edits without writing.
//
// `fileId` must be the patient's BQ file id (which is what the backend
// joins on — both `referrals[].file_id` from intake and `crm_patients
// .bq_file_id` from the queue map to the same string).
// 2026-10-24 (card-panels-refetch, condition 7): an empty save over existing codes asks first.
export const emptySavePrompt = (n) =>
  n === 1
    ? "This removes the 1 ICD-10 code from this referral (and from Raintree). Remove it?"
    : `This removes all ${n} ICD-10 codes from this referral (and from Raintree). Remove them?`;

export function Icd10Panel({ codes, review, fileId, userRole, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [editCodes, setEditCodes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  // Read-after-write guard (2026-08-25). The save's BigQuery UPDATE runs
  // in a FastAPI BackgroundTask AFTER the 2xx, but the parent's refresh
  // reads `weststar_documents` straight back — so the refetch returns
  // the PRE-save row and the list looked unchanged until a later manual
  // refresh. The codes we just saved therefore win locally until the
  // parent hands back a list that actually matches them.
  const [savedOverride, setSavedOverride] = useState(null);

  const propCodes = Array.isArray(codes) ? codes : [];
  const initial = savedOverride || propCodes;
  // Item 3 (2026-09-20): the clinic front desk joined the confirmers. It
  // reads the codes off the paperwork, so it is who should be able to clear
  // the ICD-10 Review badge; the PUT's role gate was widened to match
  // (intake_router.save_icd10_codes) and the Portal Patch webhook is
  // role-blind, so a location confirm fires the same patch.
  const canEdit = userRole === "admin" || userRole === "sales" || userRole === "location";

  // Server caught up (or someone else edited) → drop the override.
  useEffect(() => {
    if (savedOverride && sameCodeList(propCodes, savedOverride)) setSavedOverride(null);
  }, [codes, savedOverride]);  // eslint-disable-line react-hooks/exhaustive-deps

  // 2026-10-24 (card-panels-refetch, Aug 25 ticket): Save waits until the editor
  // holds the referral's CURRENT codes, so an editor that opened empty or stale
  // can't be saved over them. Admin and sales read them from the intake record
  // (GET /api/intake/referrals/{fileId}). A location login can't (the server
  // denies that read for the role), so it edits the card's codes, which every
  // ICD-10 save writes first (Postgres, before BigQuery). Our own save in this
  // panel can be newer than the read (BigQuery is written after the answer),
  // so `savedOverride` wins over a read that hasn't caught up yet.
  const canLoad = userRole === "admin" || userRole === "sales";
  const [loadState, setLoadState] = useState("ready"); // "loading" | "ready" | "failed"
  const [currentCodes, setCurrentCodes] = useState([]);
  const loadSeq = useRef(0);
  const toEdit = (list) => list.map((c) => ({ code: c.code, description: c.description || "" }));

  const beginEdit = async () => {
    const seq = ++loadSeq.current;
    setSaveError(null);
    setEditing(true);
    if (!canLoad) {
      // The card must actually carry the field: missing (undefined/null) is not "no codes".
      const fromCard = savedOverride || (Array.isArray(codes) ? codes : null);
      if (!fromCard) { setEditCodes([]); setLoadState("failed"); return; }
      setCurrentCodes(fromCard);
      setEditCodes(toEdit(fromCard));
      setLoadState("ready");
      return;
    }
    setEditCodes([]);
    setLoadState("loading");
    try {
      if (!fileId) throw new Error("missing file id");
      const res = await axios.get(`${API}/api/intake/referrals/${fileId}`, {
        headers: { Authorization: `Bearer ${getStoredToken()}` },
        withCredentials: true,
      });
      const read = res && res.data ? res.data.icd10_codes : null;
      if (!Array.isArray(read)) throw new Error("no codes in the answer");
      if (seq !== loadSeq.current) return;
      const current = savedOverride && !sameCodeList(read, savedOverride) ? savedOverride : read;
      setCurrentCodes(current);
      setEditCodes(toEdit(current));
      setLoadState("ready");
    } catch {
      if (seq === loadSeq.current) setLoadState("failed");
    }
  };
  const cancelEdit = () => { loadSeq.current += 1; setEditing(false); setSaveError(null); };

  const addCode = (ref) => setEditCodes((cur) =>
    cur.some((c) => c.code === ref.code)
      ? cur
      : [...cur, { code: ref.code, description: ref.long_desc }]
  );
  const removeCode = (idx) => setEditCodes((cur) => cur.filter((_, i) => i !== idx));
  const moveCode = (idx, delta) => setEditCodes((cur) => {
    const next = [...cur];
    const swap = idx + delta;
    if (swap < 0 || swap >= next.length) return cur;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    return next;
  });

  const handleSave = async () => {
    if (saving) return;                       // double-submit guard
    if (loadState !== "ready") return;        // 2026-10-24: only over the current codes
    if (editCodes.length === 0 && currentCodes.length > 0 && !window.confirm(emptySavePrompt(currentCodes.length))) return;
    if (!fileId) { setSaveError("Missing referral file id"); return; }
    setSaving(true);
    setSaveError(null);
    try {
      await axios.put(
        `${API}/api/intake/referrals/${fileId}/icd10`,
        { codes: editCodes },
        {
          headers: { Authorization: `Bearer ${getStoredToken()}` },
          withCredentials: true,
        },
      );
      // Show the write immediately — the parent's refetch reads BQ,
      // which the background UPDATE hasn't reached yet.
      setSavedOverride(editCodes.map((c) => ({ code: c.code, description: c.description || "" })));
      setEditing(false);
      // Parent refresh — clears the badge and surfaces the new list
      // without a full page reload. Pass the saved codes so the parent
      // can apply an optimistic update (it may not re-fetch
      // synchronously).
      onUpdate?.(editCodes);
    } catch (err) {
      // No override on failure: the list keeps showing the server state
      // and the panel stays in edit mode with the error visible.
      setSaveError(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // ── VIEW MODE ──
  if (!editing) {
    const empty = initial.length === 0;
    return (
      <div data-testid="icd10-panel" className="p-4 bg-slate-50 rounded-lg border border-slate-200">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-slate-700">ICD-10 Codes</h4>
          <div className="flex items-center gap-2">
            <Icd10ReviewBadge show={review && !savedOverride} />
            {canEdit && (
              <button
                type="button"
                onClick={beginEdit}
                data-testid="icd10-edit-btn"
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200"
              >
                <Edit2 size={12} /> Edit
              </button>
            )}
          </div>
        </div>
        {empty ? (
          <p className="text-sm text-slate-400 italic" data-testid="icd10-empty">No codes / needs review</p>
        ) : (
          <ul className="space-y-1" data-testid="icd10-list">
            {initial.map((c, i) => (
              <li key={`${c.code}-${i}`} className="text-sm text-slate-800 font-mono flex gap-3"
                  data-testid={`icd10-item-${c.code}`}>
                <span className="font-semibold text-slate-900 min-w-[64px]">{c.code}</span>
                {c.description ? <span className="text-slate-600 font-sans">— {c.description}</span> : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  // ── EDIT MODE ──
  const excludeCodes = editCodes.map((c) => c.code);
  return (
    <div data-testid="icd10-panel-edit" className="p-4 bg-amber-50 rounded-lg border border-amber-300">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-slate-700">Edit ICD-10 Codes</h4>
        <p className="text-xs text-slate-500 italic">First code = primary</p>
      </div>
      {loadState !== "ready" && (
        <p className={`text-xs mb-3 ${loadState === "failed" ? "text-red-700" : "text-slate-500"}`} data-testid="icd10-load-state">
          {loadState === "failed"
            ? "The current codes couldn't be loaded. Refresh the page, then try again."
            : "Loading the current codes…"}
        </p>
      )}
      <ul className="space-y-1 mb-3" data-testid="icd10-edit-list">
        {loadState === "ready" && editCodes.length === 0 && (
          <li className="text-sm text-slate-400 italic px-2 py-1">No codes yet — add via search below.</li>
        )}
        {editCodes.map((c, i) => (
          <li
            key={`${c.code}-${i}`}
            className="flex items-center gap-2 px-2 py-1.5 bg-white rounded border border-slate-200"
            data-testid={`icd10-edit-item-${c.code}`}
          >
            <span className="text-[10px] text-slate-400 font-medium w-4">{i + 1}.</span>
            <span className="font-mono font-semibold text-slate-900 min-w-[80px] text-sm">{c.code}</span>
            <span className="text-slate-600 text-sm flex-1 truncate">{c.description}</span>
            <button type="button" disabled={i === 0} onClick={() => moveCode(i, -1)}
                    data-testid={`icd10-up-${c.code}`}
                    className="px-1.5 py-0.5 text-xs text-slate-500 hover:bg-slate-100 rounded disabled:opacity-30">↑</button>
            <button type="button" disabled={i === editCodes.length - 1} onClick={() => moveCode(i, 1)}
                    data-testid={`icd10-down-${c.code}`}
                    className="px-1.5 py-0.5 text-xs text-slate-500 hover:bg-slate-100 rounded disabled:opacity-30">↓</button>
            <button type="button" onClick={() => removeCode(i)}
                    data-testid={`icd10-remove-${c.code}`}
                    className="px-1.5 py-0.5 text-xs text-red-600 hover:bg-red-50 rounded"><X size={12} /></button>
          </li>
        ))}
      </ul>
      <Icd10SearchBox onSelect={addCode} excludeCodes={excludeCodes} disabled={saving || loadState !== "ready"} />
      {saveError && (
        <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 mt-3" data-testid="icd10-save-error">
          {saveError}
        </p>
      )}
      <div className="flex justify-end gap-2 mt-3">
        <button
          type="button"
          onClick={cancelEdit}
          disabled={saving}
          data-testid="icd10-cancel-btn"
          className="px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || loadState !== "ready"}
          data-testid="icd10-save-btn"
          className="px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          {saving ? <><Loader2 size={12} className="animate-spin" /> Saving…</> : "Save"}
        </button>
      </div>
    </div>
  );
}
